// services/guestListService.js
//
// The file was here and it was empty — zero bytes, never committed with
// anything in it. Two modules import `getUserGuestLists` from it
// (create/GuestListSelector and create/RSVPSection), and an ES import of a
// name a module does not export is `undefined` rather than an error, so the
// failure only showed up at the moment of use: opening Guest lists in the RSVP
// step of the create flow threw "getUserGuestLists is not a function" and the
// picker never loaded. Webpack reports it as one warning among two hundred,
// which is how it stayed invisible.
//
// The query is the one GuestLists.js already runs inline, moved here so the
// two callers and the page agree on what a guest list looks like — in
// particular on `contact_count`, which Supabase returns as `[{count: n}]` from
// an aggregate in the select list and which every caller then has to flatten.

import supabase from './supabase';

/**
 * The signed-in user's guest lists, newest first.
 *
 * @returns {Promise<Array<{id: string, name: string, contact_count: number}>>}
 *   An empty array when nobody is signed in — the create flow is reachable
 *   signed out now, and a picker with nothing in it is a truer answer there
 *   than an exception.
 */
export async function getUserGuestLists() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('guest_lists')
    .select('*, contact_count:guest_list_contacts(count)')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // `contact_count:guest_list_contacts(count)` comes back as [{count: n}], or
  // as an empty array for a list with no contacts yet.
  return (data ?? []).map((list) => ({
    ...list,
    contact_count: list.contact_count?.[0]?.count ?? 0,
  }));
}

/** One guest list with its contacts, or null if it is not the caller's. */
export async function getGuestListById(id) {
  const { data, error } = await supabase
    .from('guest_lists')
    .select('*, contacts:guest_list_contacts(*)')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}

const guestListService = { getUserGuestLists, getGuestListById };
export default guestListService;
